import {
  AlertTriangle,
  Briefcase,
  CarFront,
  Gavel,
  HeartPulse,
  IdCard,
  Mail,
  MapPin,
  Phone,
  UserRound,
} from "lucide-react";
import type { Driver, LeadQuote } from "@ir/types";
import {
  formatAddress,
  formatCurrency,
  formatDate,
  formatYesNo,
  fullName,
} from "@ir/services";
import { Badge, Card, CardHeader, DetailList, EmptyState } from "@ir/ui";

/**
 * One driver's full record.
 *
 * Sub-sections only render when the driver actually has that kind of history,
 * so a clean record is a short page rather than eight empty headings.
 */
export function DriverPanel({
  driver,
  quote,
  title,
  isMain = false,
}: {
  driver: Driver;
  quote: LeadQuote;
  title: string;
  isMain?: boolean;
}) {
  const flags = [
    driver.has_criminal_conviction && { label: "Criminal convictions", tone: "warning" as const },
    driver.has_conviction && { label: "Driving convictions", tone: "warning" as const },
    driver.has_incident && { label: "Motor claims", tone: "warning" as const },
    driver.has_medical_condition && { label: "Medical conditions", tone: "info" as const },
    driver.has_insurance_refused && { label: "Insurance refused", tone: "danger" as const },
  ].filter(Boolean) as { label: string; tone: "warning" | "info" | "danger" }[];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          icon={UserRound}
          title={title}
          description={fullName([driver.title, driver.first_name, driver.last_name])}
          actions={
            flags.length > 0 ? (
              <div className="flex flex-wrap justify-end gap-1.5">
                {flags.map((flag) => (
                  <Badge key={flag.label} tone={flag.tone} icon={AlertTriangle}>
                    {flag.label}
                  </Badge>
                ))}
              </div>
            ) : (
              <Badge tone="success" dot>
                Clean record
              </Badge>
            )
          }
        />

        <DetailList
          items={[
            {
              label: (
                <span className="inline-flex items-center gap-1.5">
                  <Phone size={11} aria-hidden="true" /> Telephone
                </span>
              ),
              value: driver.telephone,
            },
            {
              label: (
                <span className="inline-flex items-center gap-1.5">
                  <Mail size={11} aria-hidden="true" /> Email
                </span>
              ),
              value: driver.email,
            },
            {
              label: (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin size={11} aria-hidden="true" /> Address
                </span>
              ),
              value: formatAddress(driver.address),
              span: 2,
            },
            { label: "Date of birth", value: formatDate(driver.date_of_birth) },
            { label: "Gender", value: driver.gender },
            { label: "Marital status", value: driver.marital_status },
            { label: "Relationship", value: driver.relationship, hideWhenEmpty: !isMain },
            { label: "Home owner", value: formatYesNo(driver.home_owner) },
            ...(isMain
              ? [
                  {
                    label: "Vehicle purchased",
                    value: formatYesNo(quote.vehicle?.[0]?.owns_vehicle),
                  },
                  { label: "CCJ", value: quote.risk?.ccj ?? quote.ccj },
                ]
              : []),
          ]}
        />
      </Card>

      {driver.licence && (
        <Card>
          <CardHeader icon={IdCard} title="Licence" />
          <DetailList
            items={[
              { label: "Type", value: driver.licence.type },
              { label: "Test pass date", value: formatDate(driver.licence.date) },
              { label: "Country", value: driver.licence.country, hideWhenEmpty: true },
              { label: "Number", value: driver.licence.number, hideWhenEmpty: true },
            ]}
          />
        </Card>
      )}

      {driver.occupation && driver.occupation.length > 0 && (
        <Card>
          <CardHeader icon={Briefcase} title="Occupation" />
          <div className="space-y-3">
            {driver.occupation.map((occupation, index) => (
              <DetailList
                key={index}
                items={[
                  { label: `Occupation ${index + 1}`, value: occupation.type },
                  { label: "Business", value: occupation.business },
                  { label: "Status", value: occupation.status, hideWhenEmpty: true },
                  {
                    label: "Part time",
                    value:
                      occupation.part_time === null || occupation.part_time === undefined
                        ? ""
                        : formatYesNo(occupation.part_time),
                    hideWhenEmpty: true,
                  },
                ]}
              />
            ))}
          </div>
        </Card>
      )}

      <Card>
        <CardHeader icon={CarFront} title="Driving" />
        <DetailList
          items={[
            {
              label: "UK resident since birth",
              value: formatYesNo(driver.is_in_uk_since_birth),
            },
            ...(driver.is_in_uk_since_birth
              ? []
              : [{ label: "UK resident since", value: formatDate(driver.uk_since) }]),
            {
              label: "Drives other vehicles",
              value: formatYesNo(driver.driving_other_vehicle),
            },
            {
              label: "Business miles",
              value:
                driver.business_miles !== null && driver.business_miles !== undefined
                  ? `${driver.business_miles} per year`
                  : "",
              hideWhenEmpty: true,
            },
            { label: "Personal use", value: formatYesNo(driver.personal_use) },
            ...(driver.personal_use
              ? [{ label: "Personal miles", value: driver.personal_miles }]
              : []),
            { label: "Commuting", value: formatYesNo(driver.commuting), hideWhenEmpty: true },
            {
              label: "Business use",
              value: formatYesNo(driver.business_use),
              hideWhenEmpty: true,
            },
          ]}
        />
      </Card>

      {driver.conviction && driver.conviction.length > 0 && (
        <Card>
          <CardHeader
            icon={Gavel}
            title="Driving convictions"
            description={`${driver.conviction.length} on record`}
          />
          <div className="divide-y divide-line">
            {driver.conviction.map((conviction, index) => (
              <div key={index} className="py-3 first:pt-0 last:pb-0">
                <DetailList
                  items={[
                    { label: `Conviction ${index + 1}`, value: conviction.type },
                    { label: "Date", value: formatDate(conviction.date) },
                    { label: "Points", value: conviction.points ?? 0 },
                    { label: "Fined", value: formatYesNo(conviction.is_fined) },
                    ...(conviction.is_fined
                      ? [{ label: "Fine", value: formatCurrency(conviction.fine ?? 0) }]
                      : []),
                    { label: "Led to a ban", value: formatYesNo(conviction.is_disqualified) },
                    ...(conviction.is_disqualified
                      ? [
                          {
                            label: "Ban length",
                            value: `${conviction.disqualification ?? 0} months`,
                          },
                        ]
                      : []),
                    { label: "Details", value: conviction.details, hideWhenEmpty: true },
                  ]}
                />
              </div>
            ))}
          </div>
        </Card>
      )}

      {driver.incident && driver.incident.length > 0 && (
        <Card>
          <CardHeader
            icon={AlertTriangle}
            title="Motor claims"
            description={`${driver.incident.length} on record`}
          />
          <div className="divide-y divide-line">
            {driver.incident.map((incident, index) => (
              <div key={index} className="py-3 first:pt-0 last:pb-0">
                <DetailList
                  items={[
                    { label: `Claim ${index + 1}`, value: incident.type },
                    { label: "Date", value: formatDate(incident.date) },
                    { label: "Claim", value: incident.claim },
                    { label: "Cost", value: formatCurrency(incident.cost ?? 0) },
                    {
                      label: "Fault",
                      value: incident.fault ? "At fault" : "Not at fault",
                    },
                    {
                      label: "NCB",
                      value: incident.ncb_affected ? "Affected" : "Not affected",
                    },
                    { label: "Anyone injured", value: formatYesNo(incident.bodily_injured) },
                    { label: "Whose policy", value: incident.policy },
                  ]}
                />
              </div>
            ))}
          </div>
        </Card>
      )}

      {driver.criminal_conviction && driver.criminal_conviction.length > 0 && (
        <Card>
          <CardHeader icon={Gavel} title="Criminal convictions" />
          <div className="divide-y divide-line">
            {driver.criminal_conviction.map((conviction, index) => (
              <div key={index} className="py-3 first:pt-0 last:pb-0">
                <DetailList
                  items={[
                    { label: `Conviction ${index + 1}`, value: conviction.reason },
                    { label: "Date", value: formatDate(conviction.date) },
                    { label: "Sentence given", value: formatYesNo(conviction.received) },
                  ]}
                />
              </div>
            ))}
          </div>
        </Card>
      )}

      {driver.medical_condition && driver.medical_condition.length > 0 && (
        <Card>
          <CardHeader icon={HeartPulse} title="Medical conditions" />
          <div className="divide-y divide-line">
            {driver.medical_condition.map((condition, index) => (
              <div key={index} className="py-3 first:pt-0 last:pb-0">
                <DetailList
                  items={[
                    { label: `Condition ${index + 1}`, value: condition.type },
                    { label: "Date", value: formatDate(condition.date) },
                    { label: "DVLA notified", value: formatYesNo(condition.dvla_notified) },
                  ]}
                />
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

export function NoAdditionalDrivers() {
  return (
    <Card padded={false}>
      <EmptyState
        icon={UserRound}
        title="No additional drivers"
        description="This quote covers the main driver only."
      />
    </Card>
  );
}
